import { Link } from 'react-router-dom';

import Humanograma from 'components/Humanograma';
import { UserAvatar } from 'components/user-avatar';

import { IconLeafSeed, IconPlus, IconSprout } from './icons';
import { Dimensao, dimensaoByBehavior, hexToRgba } from './mappings';

/**
 * Núcleo compartilhado da Galeria do Reconhecimento: agrega o feed de
 * reconhecimentos por pessoa e renderiza o card "hero" da pessoa reconhecida.
 * Usado tanto no Dashboard ("Últimos reconhecidos") quanto na página Galeria.
 */

export interface GalleryEntry {
  playerId: string;
  name: string;
  nomeGuerra?: string;
  funcao?: string;
  unidade?: string;
  avatar?: any;
  total: number;
  lastAt: number;
  dimensao: Dimensao;
}

/**
 * Agrega o feed de reconhecimentos do ciclo por pessoa reconhecida: total
 * recebido, último recebimento e dimensão dominante (que dá a cor do card).
 */
export function buildGallery(feed: any[]): GalleryEntry[] {
  const byPlayer: Record<string, any> = {};

  feed.forEach(item => {
    if (!item?.target) return;
    if (!byPlayer[item.target]) {
      byPlayer[item.target] = {
        playerId: item.target,
        name: item.target_name,
        nomeGuerra: item.target_nome_guerra,
        funcao: item.target_funcao,
        unidade: item.target_desc_unidade || item.target_diretoria || item.target_nome_dependencia,
        avatar: item.target_avatar,
        total: 0,
        lastAt: 0,
        dimCounts: {} as Record<string, number>,
      };
    }
    const entry = byPlayer[item.target];
    entry.total += 1;
    entry.dimCounts[item.behavior] = (entry.dimCounts[item.behavior] || 0) + 1;
    entry.lastAt = Math.max(entry.lastAt, new Date(item.time).getTime());
  });

  return Object.values(byPlayer)
    .map((entry: any) => {
      const topBehavior = Object.keys(entry.dimCounts).sort((a, b) => entry.dimCounts[b] - entry.dimCounts[a])[0];
      const { dimCounts, ...rest } = entry;
      return { ...rest, dimensao: dimensaoByBehavior(topBehavior) };
    })
    .sort((a, b) => b.lastAt - a.lastAt);
}

const MEDAL_COLORS: Record<number, string> = { 1: '#f6c94a', 2: '#cfd6e0', 3: '#e2894a' };

export function PersonSeedCard({ entry, index, rank }: { entry: GalleryEntry; index: number; rank?: number | null }) {
  const accent = entry.dimensao.color;

  return (
    <div
      className="rb-hero rb-rise"
      style={{ ['--acc' as any]: accent, ['--acc-glow' as any]: hexToRgba(accent, 0.55), animationDelay: `${index * 70}ms` }}
    >
      <div className="rb-hero__inner">
        {!!rank && (
          <span className="rb-hero__rank" style={{ background: MEDAL_COLORS[rank] || '#cfd6e0' }}>
            {rank}º
          </span>
        )}

        <div className="rb-hero__watermark">
          <IconLeafSeed color={accent} size={68} />
        </div>

        <div className="rb-hero__avatar">
          <div className="rb-hero__avatar-ring" style={{ boxShadow: `0 0 0 2px ${accent}, 0 0 24px ${hexToRgba(accent, 0.55)}` }}>
            <UserAvatar data={{ ...entry.avatar, ariaHidden: true }} responsive={true} />
          </div>
          <div className="rb-seedbadge" style={{ background: accent }}>
            <IconSprout color="#fff" size={15} />
          </div>
        </div>

        <Humanograma data={{ nome: entry.name, funcao: entry.funcao, _id: entry.playerId }}>
          <div className="rb-hero__name">{entry.nomeGuerra || entry.name?.split(' ')[0]}</div>
        </Humanograma>
        {!!entry.unidade && <div className="rb-hero__unit">{entry.unidade}</div>}
        {!!entry.funcao && <div className="rb-hero__role">{entry.funcao}</div>}

        <div
          className="rb-dimchip rb-dimchip--dark rb-hero__dimchip"
          style={{
            color: accent,
            background: hexToRgba(accent, 0.14),
            boxShadow: `inset 0 0 0 1.5px ${hexToRgba(accent, 0.5)}`,
          }}
        >
          <span className="rb-dimchip__dot" style={{ background: accent }} />
          {entry.dimensao.label}
        </div>

        <div
          className="rb-hero__count"
          style={{ background: hexToRgba(accent, 0.15), boxShadow: `inset 0 0 0 1px ${hexToRgba(accent, 0.55)}` }}
        >
          <IconSprout color={accent} size={16} />
          <span className="rb-hero__count-value">{entry.total}</span>
          <span className="rb-hero__count-label">reconhecimento{entry.total === 1 ? '' : 's'}</span>
        </div>

        <div className="rb-hero__spacer" />

        <Link to={`/perfil/${entry.playerId}`} className="rb-vermais">
          <IconPlus size={15} /> Ver mais
        </Link>
      </div>
    </div>
  );
}
